import { useMutation } from '@tanstack/react-query';
import PQueue from 'p-queue';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TokenService } from '../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../../services/network/file-upload';
import { WebApi } from '../../../../services/web-api';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { GeodataSetupContextType } from '../../../state/geodata-setup-state';
import { CreateStepStatus } from './use-create-geodata-step';

export type UploadStepStatus = 'pending' | 'loading' | 'success' | 'error';

type BoundaryPolygonFile = {
  filename: string;
  file: File;
};

type UploadBoundaryPolygonParameters = {
  file: File;
  filename: string;
  geodataId: string;
  onSuccess: () => void;
  onError: () => void;
};

export const useUploadBoundaryPolygonsStep = (
  context: GeodataSetupContextType | undefined,
  geodataId: string | undefined,
  createStepStatus: CreateStepStatus,
) => {
  const { buildErrorList, handleError, clearErrors } = useErrorHandling({
    displayNonApiErrors: false,
  });
  const uploadInitiatedRef = useRef(false);

  const boundaryFiles = context?.boundaryFiles || [];
  const boundaryUploadQueue = useBoundaryPolygonUploadQueue(boundaryFiles, geodataId);

  const retry = useCallback(() => {
    uploadInitiatedRef.current = false;
    boundaryUploadQueue.retryFailed();
  }, [boundaryUploadQueue]);

  const errorList = useMemo(() => {
    return buildErrorList({ retryCallback: retry });
  }, [buildErrorList, retry]);

  // Map hook status to StepStatus
  const uploadStepStatus = useMemo<UploadStepStatus>(() => {
    if (boundaryUploadQueue.status === 'idle' && !boundaryFiles.length) {
      return 'success';
    }
    if (boundaryUploadQueue.status === 'idle') {
      return 'pending';
    }
    if (boundaryUploadQueue.status === 'loading') {
      return 'loading';
    }
    if (boundaryUploadQueue.status === 'success') {
      return 'success';
    }
    return 'error';
  }, [boundaryUploadQueue.status, boundaryFiles.length]);

  // Calculate progress from completed count
  const progress = useMemo(() => {
    return boundaryUploadQueue.totalCount > 0
      ? Math.floor((100 * boundaryUploadQueue.completedCount) / boundaryUploadQueue.totalCount)
      : 0;
  }, [boundaryUploadQueue.totalCount, boundaryUploadQueue.completedCount]);

  // Handle errors from the upload queue
  useEffect(() => {
    if (boundaryUploadQueue.error) {
      handleError(boundaryUploadQueue.error);
    } else if (boundaryUploadQueue.status === 'success') {
      clearErrors();
    }
  }, [boundaryUploadQueue.error, boundaryUploadQueue.status, handleError, clearErrors]);

  // Auto-start upload when conditions are met
  useEffect(() => {
    if (!geodataId || !context) {
      return;
    }

    // Prevent duplicate upload initiation in React Strict Mode
    if (uploadInitiatedRef.current || boundaryUploadQueue.status !== 'idle') {
      return;
    }

    // Step 2: Upload boundary polygons (only if create step succeeded and there are files)
    if (createStepStatus === 'success') {
      if (boundaryFiles.length > 0) {
        uploadInitiatedRef.current = true;
        boundaryUploadQueue.startUpload();
      }
      // If no boundary files, the status will remain 'idle' which maps to 'success' in the component
    }
  }, [geodataId, context, createStepStatus, boundaryFiles.length, boundaryUploadQueue]);

  // Reset upload initiation when geodataId changes
  useEffect(() => {
    uploadInitiatedRef.current = false;
  }, [geodataId]);

  return {
    boundaryUploadQueue,
    uploadStepStatus,
    progress,
    errorList,
    retry,
  };
};

/**
 * Note: Boundary polygon uploads use concurrency of 3
 */
const defaultConcurrentUploads = 3;

async function uploadBoundaryPolygon(params: UploadBoundaryPolygonParameters) {
  // Upload the file
  const upload = new FileUpload({
    apiBaseUrl: WebApi.getUrl(),
    authToken: new TokenService().get(),
    file: params.file,
    concurrency: 1,
  });

  const { id: uploadId } = await upload.start();

  // Create the boundary polygon record
  await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().createBoundaryPolygon({
    path: {
      geodataId: params.geodataId,
    },
    body: {
      uploadId: uploadId,
      boundaryType: 'Outer',
      fileName: params.filename,
    },
  });
}

type UploadStatus = 'idle' | 'loading' | 'success' | 'error';

type UseBoundaryPolygonUploadQueueResult = {
  status: UploadStatus;
  completedCount: number;
  totalCount: number;
  currentIndex: number;
  error: Error | null;
  startUpload: () => void;
  retryFailed: () => void;
};

function useBoundaryPolygonUploadQueue(
  boundaryFiles: BoundaryPolygonFile[],
  geodataId: string | undefined,
): UseBoundaryPolygonUploadQueueResult {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [completedCount, setCompletedCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);
  const completedCountRef = useRef(0);
  const hasFailedRef = useRef(false);
  const successfulIndicesRef = useRef<Set<number>>(new Set());
  const failedIndicesRef = useRef<Set<number>>(new Set());

  const totalCount = boundaryFiles.length;

  const pQueue = useRef(
    new PQueue({
      concurrency: defaultConcurrentUploads,
      autoStart: true,
    }),
  );

  const { mutateAsync } = useMutation({
    mutationFn: async (params: UploadBoundaryPolygonParameters) => {
      return uploadBoundaryPolygon(params);
    },
    onError: (error, variables) => {
      if (!cancelledRef.current) {
        console.error('Failed to upload boundary polygon:', error);
        variables.onError();
      }
    },
    onSuccess: (_, variables) => {
      if (!cancelledRef.current) {
        variables.onSuccess();
      }
    },
  });

  const queueUploads = useCallback(
    (indicesToUpload: number[], geodataIdToUse: string) => {
      indicesToUpload.forEach((i) => {
        const { file, filename } = boundaryFiles[i];
        const index = i + 1;
        void pQueue.current.add(
          async () =>
            await mutateAsync({
              file,
              filename,
              geodataId: geodataIdToUse,
              onSuccess: () => {
                if (!cancelledRef.current) {
                  successfulIndicesRef.current.add(index);
                  failedIndicesRef.current.delete(index);
                  completedCountRef.current++;
                  setCompletedCount(completedCountRef.current);
                  setCurrentIndex(index);

                  // Check if all uploads are complete
                  const totalProcessed =
                    successfulIndicesRef.current.size + failedIndicesRef.current.size;
                  if (totalProcessed >= totalCount && !cancelledRef.current) {
                    if (failedIndicesRef.current.size > 0) {
                      setError(new Error('One or more boundary polygons failed to upload'));
                      setStatus('error');
                    } else {
                      setStatus('success');
                    }
                  }
                }
              },
              onError: () => {
                if (!cancelledRef.current) {
                  failedIndicesRef.current.add(index);
                  successfulIndicesRef.current.delete(index);
                  hasFailedRef.current = true;
                  completedCountRef.current++;
                  setCompletedCount(completedCountRef.current);
                  setCurrentIndex(index);

                  // Check if all uploads are complete (even if some failed)
                  const totalProcessed =
                    successfulIndicesRef.current.size + failedIndicesRef.current.size;
                  if (totalProcessed >= totalCount && !cancelledRef.current) {
                    setError(new Error('One or more boundary polygons failed to upload'));
                    setStatus('error');
                  }
                }
              },
            }),
        );
      });
    },
    [boundaryFiles, totalCount, mutateAsync],
  );

  const startUpload = useCallback(() => {
    if (!geodataId || !boundaryFiles.length || status === 'loading') {
      return;
    }

    setStatus('loading');
    setCompletedCount(0);
    setCurrentIndex(0);
    setError(null);
    cancelledRef.current = false;
    completedCountRef.current = 0;
    hasFailedRef.current = false;
    successfulIndicesRef.current = new Set();
    failedIndicesRef.current = new Set();

    try {
      // Queue all uploads
      const allIndices = boundaryFiles.map((_, i) => i);
      queueUploads(allIndices, geodataId);
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('Failed to queue boundary polygon uploads:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setStatus('error');
      }
    }
  }, [geodataId, boundaryFiles, status, queueUploads]);

  const retryFailed = useCallback(() => {
    if (!geodataId || !boundaryFiles.length || status === 'loading') {
      return;
    }

    const failedIndices = Array.from(failedIndicesRef.current).map((index) => index - 1); // Convert to 0-based
    if (failedIndices.length === 0) {
      return;
    }

    setStatus('loading');
    setError(null);
    cancelledRef.current = false;
    hasFailedRef.current = false;
    // Clear failed indices but keep successful ones
    failedIndicesRef.current = new Set();

    try {
      // Queue only failed uploads for retry
      queueUploads(failedIndices, geodataId);
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('Failed to queue boundary polygon uploads for retry:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setStatus('error');
      }
    }
  }, [geodataId, boundaryFiles.length, status, queueUploads]);

  // Cleanup on unmount
  useEffect(() => {
    const queue = pQueue.current;
    return () => {
      cancelledRef.current = true;
      queue.clear();
    };
  }, []);

  return {
    status,
    completedCount,
    totalCount,
    currentIndex,
    error,
    startUpload,
    retryFailed,
  };
}
